import { useState } from 'react';
import { 
  FaFilter, 
  FaSyncAlt, 
  FaTimes, 
  FaSearch,
  FaSortAmountDown,
  FaSortAmountUp,
  FaBuilding
} from 'react-icons/fa';

const IssuerFilters = ({ 
  filters, 
  onFilterChange, 
  onReset, 
  loading,
  isSearching 
}) => {
  // ✅ États locaux complètement indépendants
  const [issuerName, setIssuerName] = useState(filters.issuerName || '');
  const [sortBy, setSortBy] = useState(filters.sortBy || 'IssuerName');
  const [sortDescending, setSortDescending] = useState(filters.sortDescending || false);

  // ✅ Fonction qui remonte TOUT l'objet filters
  const notifyChange = (updates) => {
    const newFilters = {
      issuerName,
      sortBy,
      sortDescending,
      ...updates
    };
    onFilterChange(newFilters);
  };

  const handleIssuerNameChange = (value) => {
    setIssuerName(value);
    notifyChange({ issuerName: value });
  };

  const handleSortByChange = (value) => {
    setSortBy(value);
    notifyChange({ sortBy: value });
  };

  const handleSortDirectionToggle = () => {
    const newValue = !sortDescending;
    setSortDescending(newValue);
    notifyChange({ sortDescending: newValue });
  };

  const handleClearSearch = () => {
    setIssuerName('');
    notifyChange({ issuerName: '' });
  };

  const handleReset = () => {
    setIssuerName('');
    setSortBy('IssuerName');
    setSortDescending(false);
    onReset();
  };

  const activeFilters = [
    issuerName,
    sortBy !== 'IssuerName' ? sortBy : '',
    sortDescending ? 'desc' : ''
  ].filter(Boolean).length;

  return (
    <div className="search-filter-container mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        
        <div className="d-flex align-items-center gap-2">
          <FaBuilding className="text-primary" size={20} />
          <h6 className="fw-bold mb-0">Émetteurs</h6>
        </div>
        
        <div className="filter-controls d-flex flex-wrap align-items-center gap-2">
          
          {/* Recherche */}
          <div className="position-relative">
            <FaSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={16} />
            <input 
              type="text" 
              className="search-input ps-4" 
              placeholder="Rechercher émetteur..." 
              value={issuerName} 
              onChange={e => handleIssuerNameChange(e.target.value)}
              disabled={loading && !isSearching}
              style={{ 
                height: '38px',
                outline: 'none',
                boxShadow: 'none',
                borderColor: issuerName ? '#0d6efd' : '#dee2e6',
                width: '220px'
              }}
            />
            {issuerName && (
              <button
                className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-2 p-0"
                onClick={handleClearSearch}
                type="button"
                disabled={loading}
                style={{ color: '#6c757d', outline: 'none', boxShadow: 'none', zIndex: 10 }}
              >
                <FaTimes size={14} />
              </button>
            )}
            {isSearching && (
              <div 
                className="position-absolute top-50 end-0 translate-middle-y me-2"
                style={{ right: issuerName ? '32px' : '8px' }}
              >
                <div className="spinner-border spinner-border-sm text-muted" style={{ width: '12px', height: '12px' }}>
                  <span className="visually-hidden">Recherche...</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Tri par */}
          <select 
            className="account-type-select" 
            onChange={(e) => handleSortByChange(e.target.value)}
            value={sortBy}
            disabled={loading}
            style={{ height: '38px', outline: 'none', width: '140px' }}
          >
            <option value="IssuerName">Nom A-Z</option>
            <option value="TotalSecurities">Nb titres</option>
            <option value="TotalValue">Valeur totale</option>
            <option value="LastIssueDate">Dernière émission</option>
          </select>
          
          {/* Direction tri */}
          <button 
            className="filter-button d-flex align-items-center gap-1"
            onClick={handleSortDirectionToggle}
            disabled={loading}
            style={{ height: '38px', padding: '0 12px', outline: 'none', minWidth: '100px' }}
          >
            {sortDescending ? <FaSortAmountDown size={14} /> : <FaSortAmountUp size={14} />}
            <span>{sortDescending ? 'Desc' : 'Asc'}</span>
          </button>
          
          {/* Reset */}
          {activeFilters > 0 && (
            <button 
              className="refresh-button d-flex align-items-center gap-1"
              onClick={handleReset}
              disabled={loading}
              style={{ height: '38px', padding: '0 12px', outline: 'none' }}
            >
              <FaSyncAlt size={14} />
              <span>Effacer</span>
            </button>
          )}
          
          {/* Badge filtres actifs */}
          {activeFilters > 0 && (
            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary rounded-pill px-2 py-1">
              {activeFilters}
            </span>
          )}
        </div>
      </div>

      {/* Filtres actifs */}
      {activeFilters > 0 && (
        <div className="mt-2 pt-2">
          <div className="d-flex align-items-center flex-wrap gap-1">
            <small className="text-muted me-2">Filtres :</small>
            {issuerName && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                {issuerName}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleIssuerNameChange('')}
                  style={{ color: '#6c757d', fontSize: '0.7rem', outline: 'none' }}
                >
                  <FaTimes size={10} />
                </button>
              </span>
            )}
            {sortBy !== 'IssuerName' && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                {sortBy === 'TotalSecurities' ? 'Nb titres' : 
                 sortBy === 'TotalValue' ? 'Valeur totale' : 
                 sortBy === 'LastIssueDate' ? 'Dernière émission' : sortBy}
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleSortByChange('IssuerName')}
                  style={{ color: '#6c757d', fontSize: '0.7rem', outline: 'none' }}
                >
                  <FaTimes size={10} />
                </button>
              </span>
            )}
            {sortDescending && (
              <span className="badge bg-light text-dark border d-flex align-items-center gap-1 px-2 py-1">
                Descendant
                <button 
                  className="btn btn-link p-0 ms-1"
                  onClick={() => handleSortDirectionToggle()}
                  style={{ color: '#6c757d', fontSize: '0.7rem', outline: 'none' }}
                >
                  <FaTimes size={10} />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default IssuerFilters;